import type { ReactNode } from 'react';

/**
 * Badge — the neutral pill for secondary metadata. Anything that carries
 * meaning belongs in StatusChip or SeverityChip, where the colour is part of
 * the message; a badge is what is left once there is nothing to signal.
 *
 * Corpus: `JC:29` (the doc header's neutral pill —
 * semibold, pill radius) for the fill, and `DB:897`
 * (pending-invite chip) for the shape: inline-flex, centred, 6px gap, 1px
 * outline. No pill in the corpus has both — the outlined ones sit on white,
 * `SI:58` likewise — so the pairing, and the #c7c7cc it borrows
 * from the input border, are this library's own rather than lifted.
 *
 * One deliberate divergence: JC:29 puts #6e6e73 on #f0f0f2, which is 4.46:1 at
 * this pill's 10.5px — normal text, and under WCAG 2.2 SC 1.4.3. The ink is
 * `--rp-ink-tertiary` (#494952, 8.85:1) instead, which the corpus itself uses on
 * the same #f0f0f2 in its neutral chip (`DB:1843`,
 * `chipNeutral`). `contrast.test.ts` reads this pairing back out of badge.css,
 * so it cannot quietly return.
 *
 * The text is a child, never a literal inside this file: every string a person
 * reads is supplied by the caller from a translation catalogue.
 */
export interface BadgeProps {
  readonly children: ReactNode;
}

export function Badge({ children }: BadgeProps) {
  return <span className="rp-badge">{children}</span>;
}
